'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import TopBar from '@/components/TopBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const benefits = [
{
  title: 'Access to 200+ Lenders',
  description: 'Our extensive network of commercial real estate lenders means you get competitive rates and terms that the open market rarely offers.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>

},
{
  title: 'Faster Closings',
  description: 'Our streamlined process and pre-established lender relationships reduce closing timelines by up to 40% compared to going direct.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>

},
{
  title: 'Maximized Leverage',
  description: 'We structure deals to maximize your loan-to-value ratio while maintaining favorable debt service coverage, preserving your capital.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>

},
{
  title: 'Expert Deal Structuring',
  description: 'From bridge loans to permanent financing, we know which structure fits your asset class, hold period, and exit strategy.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>

},
{
  title: 'Full-Spectrum Asset Coverage',
  description: 'Office, retail, industrial, multifamily, hospitality, mixed-use — we finance every commercial asset class with equal expertise.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>

},
{
  title: 'Transparent Fee Structure',
  description: 'No hidden costs. Our fee structure is disclosed upfront so you can model your deal economics with complete accuracy.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>

}];


const processSteps = [
{
  step: '01',
  title: 'Initial Consultation',
  description: 'We review your property details, investment thesis, and financing goals. This 30-minute call shapes the entire strategy.'
},
{
  step: '02',
  title: 'Deal Analysis & Packaging',
  description: 'Our team prepares a comprehensive loan package — financials, rent rolls, market comps — designed to present your deal in its strongest light.'
},
{
  step: '03',
  title: 'Lender Matching',
  description: 'We identify the 3–5 lenders from our network whose appetite, geography, and pricing best match your specific asset and deal profile.'
},
{
  step: '04',
  title: 'Term Sheet Negotiation',
  description: 'We negotiate competing term sheets simultaneously, leveraging offers against each other to drive down rates and improve covenants.'
},
{
  step: '05',
  title: 'Due Diligence Support',
  description: `We manage the lender's due diligence process end-to-end — appraisals, environmental reports, title — keeping everything on schedule.`
},
{
  step: '06', title: 'Closing & Funding', description: 'We coordinate with attorneys, title companies, and lenders to ensure a clean, on-time closing with no last-minute surprises.'
}];


const assetClasses = [
'Office Buildings',
'Retail Centers & Strip Malls',
'Industrial & Warehouse',
'Multifamily (5+ Units)',
'Mixed-Use Developments',
'Hospitality & Hotels',
'Self-Storage Facilities',
'Medical & Healthcare'];


function AnimatedSection({ children, className = '' }: {children: React.ReactNode;className?: string;}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {if (entry.isIntersecting) setVisible(true);}, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </div>);

}

export default function CommercialRealEstateFinancePage() {
  return (
    <main className="min-h-screen font-sans bg-white">
      <TopBar />
      <Header />

      {/* Hero */}
      <section className="relative w-full min-h-[480px] flex items-center justify-center overflow-hidden">
        <Image
          src="https://img.rocket.new/generatedImages/rocket_gen_img_1dc059208-1772065007142.png"
          alt="Commercial real estate building aerial view"
          fill
          className="object-cover"
          priority />
        
        <div className="absolute inset-0 bg-brand-darkbg/75" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-24">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-brand-green" />
            <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Service 01</span>
            <div className="h-px w-12 bg-brand-green" />
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-medium text-white mb-6 leading-tight">
            Commercial Real Estate Finance
          </h1>
          <p className="font-sans text-gray-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Specialized financing solutions for acquisitions, development, and refinancing — structured to maximize leverage and minimize risk.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-green text-white rounded-md font-sans font-semibold text-sm hover:bg-green-600 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-green/30">
              
              Get Financing Today
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              href="/#services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white rounded-md font-sans font-semibold text-sm hover:bg-white/10 transition-all duration-200">
              
              All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Deep Dive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-0.5 bg-brand-green" />
                <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">What We Do</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-navy mb-6">
                Financing Built Around Your Asset, Not a Template
              </h2>
              <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
                Grid Global provides expert commercial real estate financing for investors, developers, and business owners across the Gulf region and beyond. We don't push you toward a pre-packaged product — we analyze your asset, your hold strategy, and your capital stack, then build a financing solution from the ground up.
              </p>
              <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
                Whether you're acquiring an office tower in Dubai, refinancing a retail center in Doha, or developing a mixed-use project from the ground up, our team has the lender relationships and structuring expertise to get your deal done at terms that work.
              </p>
              <p className="font-sans text-gray-600 text-base leading-relaxed">
                We work with banks, debt funds, insurance companies, and private lenders — giving you access to the full spectrum of commercial real estate capital, not just what one institution offers.
              </p>
            </AnimatedSection>
            <AnimatedSection className="grid grid-cols-2 gap-4">
              {assetClasses.map((asset, i) =>
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-brand-green/30 hover:bg-brand-green/5 transition-all duration-200">
                  <div className="w-2 h-2 rounded-full bg-brand-green flex-shrink-0" />
                  <span className="font-sans text-sm text-brand-navy font-medium">{asset}</span>
                </div>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-brand-darkbg">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-brand-green" />
              <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Why Grid Global</span>
              <div className="h-px w-12 bg-brand-green" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-white">
              The Advantages of Working With Us
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) =>
            <AnimatedSection key={i} className="p-6 rounded-2xl bg-brand-altbg border border-white/10 hover:border-brand-green/40 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center text-brand-green mb-5 group-hover:bg-brand-green group-hover:text-white transition-all duration-300">
                  {benefit.icon}
                </div>
                <h3 className="font-serif text-lg font-medium text-white mb-3">{benefit.title}</h3>
                <p className="font-sans text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
              </AnimatedSection>
            )}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-brand-green" />
              <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Our Process</span>
              <div className="h-px w-12 bg-brand-green" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-navy">
              From First Call to Funded Deal
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, i) =>
            <AnimatedSection key={i} className="relative">
                <div className="p-8 rounded-2xl border border-gray-100 hover:border-brand-navy/20 hover:shadow-lg transition-all duration-300 h-full">
                  <div className="font-serif text-5xl font-medium text-brand-navy/10 mb-4 leading-none">{step.step}</div>
                  <h3 className="font-serif text-xl font-medium text-brand-navy mb-3">{step.title}</h3>
                  <p className="font-sans text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </AnimatedSection>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-white mb-5">
              Ready to Finance Your Next Commercial Property?
            </h2>
            <p className="font-sans text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
              Tell us about your deal. Our team will respond within 24 hours with a preliminary financing strategy tailored to your asset.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-green text-white rounded-md font-sans font-semibold text-sm hover:bg-green-600 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-green/30">
                
                Start Your Application
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                href="/#services"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white rounded-md font-sans font-semibold text-sm hover:bg-white/10 transition-all duration-200">
                
                Explore Other Services
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </main>);

}